/**
 * Cambio de sueldo PLAME a partir de un día (Consideraciones Previas).
 * Hasta el día anterior: sueldo_1; desde fecha_cambio: sueldo_2.
 * El 0121 y la base de extras/AFP/ONP/EsSalud usan el monto prorrateado.
 */

import { montoProporcionalPorDias, calcularDiasEfectivosIngresoCese } from './ingresosDiasEspeciales';

export function mapaSueldoTramosDesdeApi(data) {
  if (data?.mapa && typeof data.mapa === 'object') {
    const out = {};
    Object.entries(data.mapa).forEach(([empId, row]) => {
      out[String(empId)] = normalizarSueldoTramo(row);
    });
    return out;
  }
  const out = {};
  (data?.tramos || []).forEach((row) => {
    if (row?.empleado_id == null) return;
    out[String(row.empleado_id)] = normalizarSueldoTramo(row);
  });
  return out;
}

export function normalizarSueldoTramo(row) {
  if (!row) return null;
  const fecha = row.fecha_cambio
    ? String(row.fecha_cambio).split('T')[0].slice(0, 10)
    : null;
  return {
    empleado_id: Number(row.empleado_id),
    activo: Boolean(row.activo),
    fecha_cambio: fecha,
    sueldo_1: row.sueldo_1 != null ? Number(row.sueldo_1) : null,
    sueldo_2: row.sueldo_2 != null ? Number(row.sueldo_2) : null,
    destino: ['plame', 'interna', 'ambas'].includes(String(row.destino || ''))
      ? String(row.destino)
      : 'plame',
  };
}

export function destinoAplicaPlame(config) {
  const d = String(config?.destino || 'plame');
  return d === 'plame' || d === 'ambas';
}

export function destinoAplicaInterna(config) {
  const d = String(config?.destino || 'plame');
  return d === 'interna' || d === 'ambas';
}

export function tramoSueldoPlameActivo(mapa, empleadoId) {
  const row = mapa?.[String(empleadoId)] || mapa?.[Number(empleadoId)];
  if (!row || !row.activo) return null;
  if (!row.fecha_cambio) return null;
  if (row.sueldo_1 == null || row.sueldo_2 == null) return null;
  return row;
}

function toYmd(d) {
  if (!d) return null;
  if (typeof d === 'string') return d.split('T')[0].slice(0, 10);
  try {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  } catch {
    return null;
  }
}

function toLocalDate(ymd) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function maxYmd(a, b) {
  if (!a) return b;
  if (!b) return a;
  return a >= b ? a : b;
}

function minYmd(a, b) {
  if (!a) return b;
  if (!b) return a;
  return a <= b ? a : b;
}

function contarDiasInclusive(desdeYmd, hastaYmd) {
  if (!desdeYmd || !hastaYmd || desdeYmd > hastaYmd) return 0;
  const a = toLocalDate(desdeYmd);
  const b = toLocalDate(hastaYmd);
  return Math.round((b - a) / (1000 * 60 * 60 * 24)) + 1;
}

/**
 * Días del período efectivo (ingreso/cese) antes y desde fecha_cambio.
 */
export function contarDiasTramosSueldo(config, analisisPeriodo, historialLaboral) {
  const fechaCambio = toYmd(config?.fecha_cambio);
  const iniPlanilla = toYmd(analisisPeriodo?.fechaInicio);
  const finPlanilla = toYmd(analisisPeriodo?.fechaFin);
  if (!fechaCambio || !iniPlanilla || !finPlanilla) {
    return { dias1: 0, dias2: 0, desde: null, hasta: null };
  }

  let desde = iniPlanilla;
  let hasta = finPlanilla;
  const ingreso = toYmd(historialLaboral?.fechaIngresoActual);
  const cese = toYmd(historialLaboral?.fechaCeseActual);
  if (ingreso) desde = maxYmd(desde, ingreso);
  if (cese) hasta = minYmd(hasta, cese);

  const diaAntes = (() => {
    const d = toLocalDate(fechaCambio);
    d.setDate(d.getDate() - 1);
    return toYmd(d);
  })();

  const dias1 = contarDiasInclusive(desde, minYmd(hasta, diaAntes));
  const dias2 = contarDiasInclusive(maxYmd(desde, fechaCambio), hasta);

  return { dias1, dias2, desde, hasta, fechaCambio };
}

export function calcularSueldoEfectivoTramos(config, analisisPeriodo, historialLaboral) {
  const { dias1, dias2, desde, hasta, fechaCambio } = contarDiasTramosSueldo(
    config,
    analisisPeriodo,
    historialLaboral
  );
  const s1 = Number(config.sueldo_1) || 0;
  const s2 = Number(config.sueldo_2) || 0;
  const monto1 = montoProporcionalPorDias(s1, dias1, analisisPeriodo);
  const monto2 = montoProporcionalPorDias(s2, dias2, analisisPeriodo);
  const monto0121 = parseFloat((monto1 + monto2).toFixed(2));

  const diasEfectivos = calcularDiasEfectivosIngresoCese(analisisPeriodo, historialLaboral);
  const totalDiasTramo = dias1 + dias2;
  // Promedio ponderado: el motor PLAME vuelve a prorratear 0121 por días efectivos.
  const sueldoEfectivo = totalDiasTramo > 0
    ? parseFloat(((s1 * dias1 + s2 * dias2) / totalDiasTramo).toFixed(2))
    : 0;

  return {
    dias1,
    dias2,
    monto1,
    monto2,
    monto0121,
    sueldoEfectivo,
    diasEfectivos,
    desde,
    hasta,
    fechaCambio,
    sueldo_1: s1,
    sueldo_2: s2,
  };
}

export function aplicarTramoSueldoAIngresos({
  conceptosIngresos = [],
  config,
  analisisPeriodo,
  historialLaboral,
}) {
  const calc = calcularSueldoEfectivoTramos(config, analisisPeriodo, historialLaboral);
  const clon = (conceptosIngresos || []).map((c) => ({
    ...c,
    plame_configuracion: c.plame_configuracion ? { ...c.plame_configuracion } : c.plame_configuracion,
  }));
  clon.forEach((c) => {
    const codigo = String(c.plame_configuracion?.codigo_plame || c.codigo_plame || c.codigoPlame || '');
    if (codigo === '0121') {
      c.monto = calc.sueldoEfectivo;
    }
  });
  return {
    conceptosIngresos: clon,
    ...calc,
  };
}

export function fechasPeriodoDeTareos(tareos) {
  const set = new Set();
  (tareos || []).forEach((t) => {
    const f = toYmd(t?.fecha);
    if (f) set.add(f);
  });
  return [...set].sort();
}

/**
 * Convierte el cambio “a partir del día X” en el formato de boleta interna partida.
 */
export function configInternaDesdeSueldoTramo(config, tareos) {
  if (!config) return null;
  const fechaCambio = toYmd(config.fecha_cambio);
  if (!fechaCambio) return null;
  const fechas = fechasPeriodoDeTareos(tareos);
  const fechasTramo1 = fechas.filter((f) => f < fechaCambio);
  if (fechasTramo1.length === 0 || fechasTramo1.length >= fechas.length) {
    return null;
  }
  return {
    empleado_id: Number(config.empleado_id) || null,
    activo: true,
    fechas_tramo_1: fechasTramo1,
    sueldo_interno_1: Number(config.sueldo_1) || 0,
    sueldo_interno_2: Number(config.sueldo_2) || 0,
    destino_descuentos_plame: 'interna_1',
  };
}

export default {
  mapaSueldoTramosDesdeApi,
  normalizarSueldoTramo,
  tramoSueldoPlameActivo,
  destinoAplicaPlame,
  destinoAplicaInterna,
  contarDiasTramosSueldo,
  calcularSueldoEfectivoTramos,
  aplicarTramoSueldoAIngresos,
  fechasPeriodoDeTareos,
  configInternaDesdeSueldoTramo,
};
